import { Box, LinearProgress, Typography } from '@mui/material';
import { FC } from 'react';
import { DocCallout, DocCodeBlock, DocSection } from '../docs-components';

const RateLimits: FC = () => (
  <Box>
    <Typography variant="h4" fontWeight={700} gutterBottom>
      Rate Limits
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      To ensure fair usage and platform stability, the Thermia API enforces rate limits on all endpoints.
    </Typography>

    <DocSection title="Default Limits">
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          '& th, & td': {
            textAlign: 'left',
            padding: '8px 12px',
            borderBottom: '1px solid',
            borderColor: 'divider',
            typography: 'body2'
          },
          '& th': { fontWeight: 700, bgcolor: 'action.hover' }
        }}>
        <thead>
          <tr>
            <th>Endpoint group</th>
            <th>Limit</th>
            <th>Window</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Device status', '60 requests', '1 minute'],
            ['Historical data', '20 requests', '1 minute'],
            ['Control commands', '10 requests', '1 minute'],
            ['Webhook management', '30 requests', '1 minute']
          ].map(([group, limit, window]) => (
            <tr key={group}>
              <td>{group}</td>
              <td>{limit}</td>
              <td>{window}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </DocSection>

    <DocSection title="Rate Limit Headers">
      <Typography variant="body1" sx={{ mb: 1.5 }}>
        Every API response includes headers indicating your current rate limit status:
      </Typography>
      <DocCodeBlock language="http">
        {`X-RateLimit-Limit: 60
X-RateLimit-Remaining: 42
X-RateLimit-Reset: 1709550120`}
      </DocCodeBlock>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
        {[
          ['X-RateLimit-Limit', 'The maximum number of requests allowed in the window.'],
          ['X-RateLimit-Remaining', 'The number of requests remaining in the current window.'],
          ['X-RateLimit-Reset', 'Unix timestamp when the current window resets.']
        ].map(([header, desc]) => (
          <Box key={header}>
            <Typography variant="body2">
              <code>{header}</code> — {desc}
            </Typography>
          </Box>
        ))}
      </Box>
    </DocSection>

    <DocSection title="Quota Usage Example">
      {[
        { label: 'Device status', used: 18, max: 60 },
        { label: 'Historical data', used: 12, max: 20 },
        { label: 'Control commands', used: 2, max: 10 }
      ].map(({ label, used, max }) => (
        <Box key={label} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">{label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {used} / {max}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(used / max) * 100}
            color={used / max > 0.8 ? 'error' : used / max > 0.5 ? 'warning' : 'primary'}
            sx={{ borderRadius: 1, height: 6 }}
          />
        </Box>
      ))}
    </DocSection>

    <DocSection title="Handling 429 Too Many Requests">
      <Typography variant="body1" sx={{ mb: 1.5 }}>
        When you exceed the rate limit the API returns a <code>429 Too Many Requests</code> response. Use the <code>Retry-After</code> header to know
        when to retry:
      </Typography>
      <DocCodeBlock language="javascript">
        {`async function fetchWithRetry(url, options, maxRetries = 3) {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const response = await fetch(url, options);

    if (response.status === 429) {
      const retryAfter = parseInt(response.headers.get('Retry-After') ?? '5', 10);
      await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
      continue;
    }

    return response;
  }
  throw new Error('Max retries exceeded');
}`}
      </DocCodeBlock>
    </DocSection>

    <DocCallout type="info">
      If you need higher rate limits for production workloads, please contact Thermia support with a description of your use case and expected request
      volumes.
    </DocCallout>
  </Box>
);

export default RateLimits;
